// src/lib/brandAccent.js
// STATUS: active | UPDATED: 2026-09-01
// LOG:
//   2026-09-01  New. A dealership's colour, made safe to actually use.
//
// Pure colour maths, no React, so it can be checked in one command against the
// real colours we have pulled off real dealership websites.
//
// WHY A STORE'S COLOUR CANNOT BE USED RAW
//
// accent.500 is not decoration in this app. It is eyebrow text on paper, a left
// rule, an active mark, and the background of a button with white text on it.
// Toyota red at #EB0A1E is a fine logo colour and fails as small text on a light
// ground. A dealership whose brand is bright yellow would ship eyebrows nobody
// can read, and it would be our fault, not theirs.
//
// So the hue is theirs and the lightness is ours. We keep what makes the colour
// recognisably their brand and move it until it is legible against the ground it
// will actually sit on, in whichever mode the person is using.
//
// That is the whole trick: nobody looks at an eyebrow and thinks "that is four
// percent darker than our logo". They think "that is our red".
//
// TARGET RATIO
//
// 4.5:1 is the WCAG AA threshold for normal text, and the eyebrows this colour
// paints are small and letterspaced, which is the hardest case in the app. If a
// colour cannot reach it in either direction we return null and the house
// oxblood stays, because a dealership's brand is not worth an illegible screen.

const LIGHT_GROUND = '#FFFFFF' // card, where eyebrows sit in light mode
const DARK_GROUND = '#141414' // night, the dark ground
const TARGET = 4.5

export function parseHex(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

function toHex({ r, g, b }) {
  const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase()
}

// WCAG relative luminance.
export function luminance({ r, g, b }) {
  const ch = (v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b)
}

export function contrast(a, b) {
  const l1 = luminance(a)
  const l2 = luminance(b)
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1]
  return (hi + 0.05) / (lo + 0.05)
}

function rgbToHsl({ r, g, b }) {
  const rr = r / 255
  const gg = g / 255
  const bb = b / 255
  const max = Math.max(rr, gg, bb)
  const min = Math.min(rr, gg, bb)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h
  if (max === rr) h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6
  else if (max === gg) h = ((bb - rr) / d + 2) / 6
  else h = ((rr - gg) / d + 4) / 6
  return { h, s, l }
}

function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const v = l * 255
    return { r: v, g: v, b: v }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (t) => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 }
}

// Walk the lightness toward the required direction, keeping hue and saturation,
// until the colour is legible against the ground. Returns null if it never gets
// there, which happens for colours with almost no saturation.
function adjustFor(hex, groundHex) {
  const rgb = parseHex(hex)
  const ground = parseHex(groundHex)
  if (!rgb || !ground) return null

  // Measure the ROUNDED hex, never the float. A candidate can clear 4.5 as
  // floating point and land at 4.49 once it is squeezed into eight bits per
  // channel, and the rounded value is the one that ships. Checked against
  // Toyota red, which failed exactly this way on the first run.
  const passes = (candidateHex) => {
    const parsed = parseHex(candidateHex)
    return parsed ? contrast(parsed, ground) >= TARGET : false
  }

  const asIs = toHex(rgb)
  if (passes(asIs)) return asIs

  const hsl = rgbToHsl(rgb)
  const towardLight = luminance(ground) < 0.2
  const step = towardLight ? 0.02 : -0.02

  let { l } = hsl
  for (let i = 0; i < 60; i += 1) {
    l += step
    // Clamp rather than bail. Breaking before testing meant a near black could
    // never be lightened at all, because its first step was still inside the
    // guard and the loop exited having tried nothing.
    l = Math.max(0.02, Math.min(0.98, l))
    const candidate = toHex(hslToRgb({ ...hsl, l }))
    if (passes(candidate)) return candidate
    if (l <= 0.02 || l >= 0.98) break
  }
  return null
}

// A tint of their colour for soft backgrounds, at the same alpha the house
// theme uses so nothing shifts weight when a dealership's colour is applied.
function softOf(hex, alpha) {
  const rgb = parseHex(hex)
  if (!rgb) return null
  return `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${alpha})`
}

// Everything the app needs to wear a dealership's colour, or null when the
// colour cannot be made legible and the house oxblood should stay.
export function brandAccent(hex) {
  const raw = parseHex(hex)
  if (!raw) return null

  const light = adjustFor(hex, LIGHT_GROUND)
  const dark = adjustFor(hex, DARK_GROUND)
  if (!light || !dark) return null

  return {
    given: toHex(raw),
    light,
    dark,
    // 600 is the pressed and hovered state, a shade further along.
    light600: adjustFor(light, LIGHT_GROUND) === light ? shade(light, -0.06) : light,
    dark600: shade(dark, 0.06),
    softLight: softOf(light, 0.08),
    softDark: softOf(dark, 0.15),
    // True when we had to move it, which is worth showing an operator so they
    // know why the screen is not exactly their hex.
    adjusted: light !== toHex(raw) || dark !== toHex(raw),
  }
}

function shade(hex, delta) {
  const rgb = parseHex(hex)
  if (!rgb) return hex
  const hsl = rgbToHsl(rgb)
  return toHex(hslToRgb({ ...hsl, l: Math.max(0.04, Math.min(0.96, hsl.l + delta)) }))
}
